import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { Form, InputGroup } from "react-bootstrap";
import * as ReactDOMServer from "react-dom/server";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import { FormInput } from "go-form";
import { Loading } from "go-core/components/Loading";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { ReactComponent as ContactBookSVG } from "../../../../../../images/svg/organization/contact-book.svg";
import { ReactComponent as PinSVG } from "../../../../../../images/svg/organization/pin.svg";
import {
	CoordinatesApi,
	GetCoordinatesApi,
	OrganizationApi,
	OrganizationFormProps,
} from "../../../../../../services/Api/types";

interface Props {
	form: UseFormReturn<OrganizationFormProps>;
	canEdit: (dataType?: "organization" | "company" | "contact") => boolean | undefined;
	coordsParams: GetCoordinatesApi;
	setCoordsParams: React.Dispatch<React.SetStateAction<GetCoordinatesApi>>;
	organization: OrganizationApi;
	getAllCountryOptions: (option: Record<string, string>) => Record<string, string>[];
	organizationCoords: CoordinatesApi;
	loadingCoords: boolean;
	hasOrganizationCoords: boolean;
}

interface RecenterProps {
	lat: number;
	lng: number;
	zoom: number;
}

const RecenterAutomatically = ({ lat, lng, zoom }: RecenterProps) => {
	const map = useMap();

	map.scrollWheelZoom.disable();
	map.invalidateSize(false);
	map.dragging.disable();
	map.touchZoom.disable();
	map.doubleClickZoom.disable();
	map.scrollWheelZoom.disable();
	map.boxZoom.disable();
	map.keyboard.disable();
	if (map.tap) map.tap.disable();

	useEffect(() => {
		map.setView([lat, lng], zoom);
	}, [lat, lng, zoom]);
	return null;
};

const OrganizationAdminOrganizationInfoForm = ({
	form,
	canEdit,
	coordsParams,
	setCoordsParams,
	organization,
	getAllCountryOptions,
	organizationCoords,
	loadingCoords,
	hasOrganizationCoords,
}: Props) => {
	const { t } = useTranslation("admin");
	const {
		register,
		formState: { errors },
		control,
	} = form;
	const mapRef = useRef<L.Map | null>(null);

	const handleChangeCoordsParams = (value: string, field: keyof GetCoordinatesApi) => {
		if (coordsParams[field] === value) return;
		const newParams: GetCoordinatesApi = { ...coordsParams };
		newParams[field] = value;
		setCoordsParams({ ...newParams });
	};

	const icon = L.divIcon({
		className: "",
		html: ReactDOMServer.renderToString(
			<div className="">
				<PinSVG className="icon" />
			</div>
		),
	});

	return (
		<fieldset>
			<h5>{t("common.word.organization_info")}</h5>
			<div className={"row"}>
				<div className={"col-md-12"}>
					<div className="place-name-input">
						<FormInput
							label={t("modules.organization.field.public_name.title")}
							name={"place.public_name"}
							errors={errors}
							placeholder={t("modules.organization.field.public_name.placeholder")}
							help={t("modules.organization.field.public_name.help_text")}
							disabled={!canEdit("organization")}
							register={register}
						/>
						<ContactBookSVG />
					</div>
				</div>
			</div>
			<div className="row">
				<div className="col-md-8">
					<div className="row">
						<div className="col-md-6">
							<FormInput
								label={t("lib:common.address.street")}
								name="place.street"
								errors={errors}
								disabled={!canEdit("organization")}
								onBlur={(e: any) => handleChangeCoordsParams(e.target.value, "street")}
								register={register}
							/>
						</div>
						<div className="col-md-3">
							<FormInput
								label={t("lib:common.address.build_nr")}
								register={register}
								name="place.build_nr"
								onBlur={(e: any) => handleChangeCoordsParams(e.target.value, "build_nr")}
								disabled={!canEdit("organization")}
								errors={errors}
							/>
						</div>
						<div className="col-md-3">
							<FormInput
								label={t("lib:common.address.flat_nr")}
								register={register}
								name="place.flat_nr"
								disabled={!canEdit("organization")}
								errors={errors}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<FormInput
								label={t("lib:common.address.city")}
								register={register}
								name="place.city"
								disabled={!canEdit("organization")}
								onBlur={(e: any) => handleChangeCoordsParams(e.target.value, "city")}
								errors={errors}
							/>
						</div>
						<div className="col-md-3">
							<FormInput
								label={t("lib:common.address.zip_code")}
								register={register}
								name="place.zip_code"
								disabled={!canEdit("organization")}
								errors={errors}
							/>
						</div>
						<div className="col-md-3">
							<FormSelectGroup
								errors={errors}
								control={control}
								label={t("lib:common.address.country")}
								name="place.country"
								disabled={!canEdit("organization")}
								options={getAllCountryOptions({
									label: organization?.place?.country,
									value: organization?.place?.country,
								})}
								getOptionLabel={(opt) => opt.label}
								getOptionValue={(opt) => opt.value}
								data-testid="place.country"
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<FormInput
								label={t("lib:common.word.email")}
								register={register}
								name="place.email"
								disabled={!canEdit("organization")}
								errors={errors}
							/>
						</div>
						<div className="col-md-6">
							<FormInput
								label={t("lib:common.word.phone_number")}
								register={register}
								name="place.phone_number"
								disabled={!canEdit("organization")}
								errors={errors}
							/>
						</div>
					</div>
				</div>
				<div className="col-md-4">
					<div
						onClick={() =>
							window.open(
								`http://maps.google.com/maps?q=${organizationCoords.latitude},${organizationCoords.longitude}`
							)
						}
					>
						<MapContainer
							zoomControl={false}
							whenCreated={(map) => (mapRef.current = map)}
							id={"organization-map"}
							scrollWheelZoom={false}
							style={{ height: "178px" }}
							center={[organizationCoords.latitude, organizationCoords.longitude]}
							zoom={16}
						>
							{loadingCoords ? (
								<Loading />
							) : (
								<>
									<TileLayer
										attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
										url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
									/>
									{hasOrganizationCoords && (
										<Marker
											icon={icon}
											position={[organizationCoords.latitude, organizationCoords.longitude]}
										/>
									)}
								</>
							)}
							<RecenterAutomatically
								lat={organizationCoords.latitude}
								lng={organizationCoords.longitude}
								zoom={hasOrganizationCoords ? 16 : 5}
							/>
						</MapContainer>
					</div>
				</div>
			</div>
			<div className="row mt-3">
				<div className="col-lg-6 col-xl-3">
					<Form.Label>{t("modules.organization.field.website.title")}</Form.Label>
					<InputGroup className="form-input-group">
						<InputGroup.Text style={{ height: "31px" }}>https.com/</InputGroup.Text>
						<FormInput
							register={register}
							name="place.website"
							disabled={!canEdit("organization")}
							errors={errors}
							style={{ borderRadius: "0 4px 4px 0" }}
						/>
					</InputGroup>
				</div>
				<div className="col-lg-6 col-xl-3 mt-auto">
					<InputGroup className="form-input-group">
						<InputGroup.Text style={{ height: "31px" }}>facebook.com/</InputGroup.Text>
						<FormInput
							register={register}
							name="place.facebook"
							disabled={!canEdit("organization")}
							errors={errors}
							style={{ borderRadius: "0 4px 4px 0" }}
						/>
					</InputGroup>
				</div>
				<div className="col-lg-6 col-xl-3 mt-auto">
					<InputGroup className="form-input-group">
						<InputGroup.Text style={{ height: "31px" }}>instagram.com/</InputGroup.Text>
						<FormInput
							register={register}
							name="place.instagram"
							disabled={!canEdit("organization")}
							errors={errors}
							style={{ borderRadius: "0 4px 4px 0" }}
						/>
					</InputGroup>
				</div>
				<div className="col-lg-6 col-xl-3 mt-auto">
					<InputGroup className="form-input-group">
						<InputGroup.Text style={{ height: "31px" }}>linkedin.com/company/</InputGroup.Text>
						<FormInput
							register={register}
							name="place.linkedin"
							disabled={!canEdit("organization")}
							errors={errors}
							style={{ borderRadius: "0 4px 4px 0" }}
						/>
					</InputGroup>
				</div>
			</div>
		</fieldset>
	);
};

export default OrganizationAdminOrganizationInfoForm;
